const META_BROWSER_PATTERN = /(FBAN|FBAV|Instagram)/i;
const ANDROID_PATTERN = /Android/i;
const IOS_PATTERN = /iPhone|iPad|iPod/i;
const TRACKING_PARAMETER_PATTERN = /^(utm_[a-z0-9_]+|fbclid|gclid|dclid|ttclid|msclkid|twclid|srsltid|_gl|_ga|ref|discount)$/i;
const ATTRIBUTION_STORAGE_KEY = 'bodega-portilla-meta-attribution';
const HANDOFF_PARAMETER = 'bp_meta_cart';
const HANDOFF_GIFT_PARAMETER = 'bp_meta_gift';

function encodeBase64Url(value) {
  const bytes = new TextEncoder().encode(value);
  let binary = '';

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

class MetaBrowserCheckoutRecovery {
  #modal;
  #externalLink;
  #closeButton;
  #lastFocusedElement = null;
  #pendingProductIds = new Set();
  #waitingProductId = '';
  #trigger = null;

  constructor() {
    this.#modal = document.querySelector('[data-meta-browser-modal]');
    this.#externalLink = this.#modal?.querySelector('[data-meta-browser-external-link]');
    this.#closeButton = this.#modal?.querySelector('[data-meta-browser-close]');

    if (!this.#modal || !this.#externalLink || !this.#closeButton) return;

    if (!this.#isMetaBrowser()) {
      this.#resumeExternalHandoff();
      return;
    }

    this.#storeAttributionParameters();
    document.addEventListener('variant:selected', this.#handleVariantSelected);
    document.addEventListener('variant:update', this.#handleVariantUpdate);
    document.addEventListener('click', this.#handleCheckoutClick, true);
    document.addEventListener('submit', this.#handleCheckoutSubmit, true);
    document.addEventListener('keydown', this.#handleKeydown);
    this.#modal.addEventListener('click', this.#handleModalClick);
    this.#modal.addEventListener('cancel', this.#handleModalCancel);
  }

  #isMetaBrowser() {
    return META_BROWSER_PATTERN.test(navigator.userAgent || '');
  }

  #handleVariantSelected = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const productId = event.target.dataset.productId;
    if (productId) this.#pendingProductIds.add(productId);
  };

  #handleVariantUpdate = (event) => {
    const productId = event.detail?.data?.productId;
    const newProductId = event.detail?.data?.newProduct?.id;
    if (productId) this.#pendingProductIds.delete(String(productId));
    if (newProductId) this.#pendingProductIds.delete(String(newProductId));

    if (!this.#waitingProductId) return;
    if (this.#waitingProductId !== String(productId) && this.#waitingProductId !== String(newProductId)) return;

    this.#waitingProductId = '';
    this.#prepareExternalLink();
  };

  #handleCheckoutClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-meta-external-checkout]');
    if (!(trigger instanceof HTMLButtonElement) || trigger.disabled) return;

    event.preventDefault();
    this.#open(trigger);
  };

  #handleCheckoutSubmit = (event) => {
    const submitter = event.submitter;
    if (!(submitter instanceof HTMLButtonElement) || !submitter.hasAttribute('data-meta-external-checkout')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#open(submitter);
  };

  #open(trigger) {
    this.#trigger = trigger;
    this.#lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!this.#modal.open) {
      if (typeof this.#modal.showModal === 'function') this.#modal.showModal();
      else this.#modal.setAttribute('open', '');
    }
    document.documentElement.classList.add('meta-browser-checkout-open');
    this.#setPlatformInstructions();

    const productForm = trigger.closest('product-form-component');
    const productId = productForm?.dataset.productId || '';
    if (trigger.dataset.metaExternalCheckout === 'product' && productId && this.#pendingProductIds.has(productId)) {
      this.#waitingProductId = productId;
      this.#setPreparing(true);
    } else {
      this.#prepareExternalLink();
    }

    this.#closeButton.focus();
  }

  #close = () => {
    if (typeof this.#modal.close === 'function' && this.#modal.open) this.#modal.close();
    else this.#modal.removeAttribute('open');
    document.documentElement.classList.remove('meta-browser-checkout-open');
    this.#waitingProductId = '';
    this.#trigger = null;
    this.#clearExternalHandoff();
    this.#lastFocusedElement?.focus();
  };

  #prepareExternalLink() {
    if (!this.#trigger || !this.#modal.open) return;

    const externalUrl = this.#buildExternalUrl(this.#trigger);
    const isAndroid = ANDROID_PATTERN.test(navigator.userAgent || '');

    this.#externalLink.href = isAndroid ? this.#buildAndroidIntent(externalUrl) : externalUrl.href;
    if (isAndroid) this.#externalLink.removeAttribute('target');
    else this.#externalLink.setAttribute('target', '_blank');
    this.#setPreparing(false);
  }

  #buildExternalUrl(trigger) {
    const cartData = this.#getCartData(trigger);
    let externalUrl;

    if (cartData.supported && cartData.lines.size > 0) {
      const lines = Array.from(cartData.lines, ([variantId, quantity]) => `${variantId}:${quantity}`).join(',');
      externalUrl = this.#createCartPermalink(lines);
      this.#setGiftProperty(externalUrl, cartData.gift);
      this.#storeExternalHandoff(lines, cartData.gift);
    } else {
      externalUrl = new URL(window.location.href);
      this.#clearExternalHandoff();
    }

    for (const [name, value] of this.#getAttributionParameters()) {
      if (!externalUrl.searchParams.has(name)) externalUrl.searchParams.set(name, value);
    }

    return externalUrl;
  }

  #getCartData(trigger) {
    const lines = new Map();
    let supported = true;
    let gift = '';

    const renderedCartSource =
      trigger.dataset.metaExternalCheckout === 'cart'
        ? trigger
        : document.querySelector('[data-meta-external-checkout="cart"][data-meta-cart-lines]');
    const cartSource =
      renderedCartSource instanceof HTMLElement && renderedCartSource.hasAttribute('data-meta-cart-lines')
        ? renderedCartSource
        : this.#modal;

    if (cartSource instanceof HTMLElement) {
      supported = cartSource.dataset.metaCartPermalinkSupported !== 'false';
      if (!this.#mergeLines(lines, cartSource.dataset.metaCartLines || '')) supported = false;
    }

    if (trigger.dataset.metaExternalCheckout === 'product') {
      const form = trigger.form || trigger.closest('form');
      const variantId = form?.querySelector('input[name="id"]')?.value?.trim();
      const quantityValue = form?.querySelector('input[name="quantity"]')?.value || '1';
      const quantity = Number.parseInt(quantityValue, 10);
      const selectedGift = form?.querySelector('input[name="properties[Muestra gratis]"]:checked');
      if (selectedGift instanceof HTMLInputElement) gift = selectedGift.value.trim().slice(0, 100);

      if (/^\d+$/.test(variantId || '') && Number.isInteger(quantity) && quantity > 0) {
        lines.set(variantId, (lines.get(variantId) || 0) + quantity);
      } else {
        supported = false;
      }
    }

    return { lines, supported, gift };
  }

  #mergeLines(lines, serializedLines) {
    if (!serializedLines) return true;

    for (const serializedLine of serializedLines.split(',')) {
      const [variantId, quantityValue] = serializedLine.split(':');
      const quantity = Number.parseInt(quantityValue, 10);
      const isValidLine =
        /^\d+$/.test(variantId || '') &&
        /^\d+$/.test(quantityValue || '') &&
        Number.isInteger(quantity) &&
        quantity > 0;
      if (!isValidLine) {
        return false;
      }
      lines.set(variantId, (lines.get(variantId) || 0) + quantity);
    }

    return true;
  }

  #createCartPermalink(lines) {
    const rootPath = this.#modal.dataset.rootUrl || '/';
    const normalizedRoot = rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
    return new URL(`${normalizedRoot}cart/${lines}`, window.location.origin);
  }

  #setGiftProperty(url, gift) {
    if (!gift) return;

    const properties = { 'Muestra gratis': gift };
    url.searchParams.set('properties', encodeBase64Url(JSON.stringify(properties)));
  }

  #storeExternalHandoff(lines, gift = '') {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set(HANDOFF_PARAMETER, lines);
    if (gift) currentUrl.searchParams.set(HANDOFF_GIFT_PARAMETER, gift);
    else currentUrl.searchParams.delete(HANDOFF_GIFT_PARAMETER);
    window.history.replaceState(window.history.state, '', currentUrl);
  }

  #clearExternalHandoff() {
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has(HANDOFF_PARAMETER)) return;
    currentUrl.searchParams.delete(HANDOFF_PARAMETER);
    currentUrl.searchParams.delete(HANDOFF_GIFT_PARAMETER);
    window.history.replaceState(window.history.state, '', currentUrl);
  }

  #resumeExternalHandoff() {
    const currentUrl = new URL(window.location.href);
    const serializedLines = currentUrl.searchParams.get(HANDOFF_PARAMETER);
    if (!serializedLines || serializedLines.length > 1200) return;

    const lines = new Map();
    if (!this.#mergeLines(lines, serializedLines) || lines.size === 0 || lines.size > 50) return;

    const normalizedLines = Array.from(lines, ([variantId, quantity]) => `${variantId}:${quantity}`).join(',');
    const externalUrl = this.#createCartPermalink(normalizedLines);
    const gift = (currentUrl.searchParams.get(HANDOFF_GIFT_PARAMETER) || '').trim().slice(0, 100);
    this.#setGiftProperty(externalUrl, gift);

    for (const [name, value] of currentUrl.searchParams) {
      if (TRACKING_PARAMETER_PATTERN.test(name)) externalUrl.searchParams.set(name, value);
    }

    window.location.replace(externalUrl.href);
  }

  #buildAndroidIntent(externalUrl) {
    const scheme = externalUrl.protocol.replace(':', '');
    const destination = `${externalUrl.host}${externalUrl.pathname}${externalUrl.search}`;
    return `intent://${destination}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(externalUrl.href)};end`;
  }

  #storeAttributionParameters() {
    const currentParameters = Array.from(new URL(window.location.href).searchParams).filter(([name]) =>
      TRACKING_PARAMETER_PATTERN.test(name)
    );
    if (currentParameters.length === 0) return;

    try {
      const storedParameters = Object.fromEntries(this.#getAttributionParameters());
      for (const [name, value] of currentParameters) storedParameters[name] = value;
      sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(storedParameters));
    } catch (error) {
      console.warn('No fue posible conservar los parámetros de atribución.', error);
    }
  }

  #getAttributionParameters() {
    const parameters = new Map();

    try {
      const storedParameters = JSON.parse(sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || '{}');
      for (const [name, value] of Object.entries(storedParameters)) {
        if (TRACKING_PARAMETER_PATTERN.test(name) && typeof value === 'string') parameters.set(name, value);
      }
    } catch (_error) {
      // The current URL below remains available when storage is blocked by the in-app browser.
    }

    for (const [name, value] of new URL(window.location.href).searchParams) {
      if (TRACKING_PARAMETER_PATTERN.test(name)) parameters.set(name, value);
    }

    return parameters;
  }

  #setPreparing(isPreparing) {
    this.#externalLink.classList.toggle('is-preparing', isPreparing);
    this.#externalLink.setAttribute('aria-disabled', isPreparing.toString());
    this.#externalLink.tabIndex = isPreparing ? -1 : 0;
    this.#externalLink.textContent = isPreparing ? 'PREPARANDO COMPRA…' : 'ABRIR EN NAVEGADOR';
    if (isPreparing) this.#externalLink.removeAttribute('href');
  }

  #setPlatformInstructions() {
    const userAgent = navigator.userAgent || '';
    const isAndroid = ANDROID_PATTERN.test(userAgent);
    const isIOS = IOS_PATTERN.test(userAgent);

    this.#modal.querySelector('[data-meta-browser-ios-instruction]')?.toggleAttribute('hidden', !isIOS);
    this.#modal.querySelector('[data-meta-browser-android-instruction]')?.toggleAttribute('hidden', !isAndroid);
    this.#modal.querySelector('[data-meta-browser-default-instruction]')?.toggleAttribute('hidden', isIOS || isAndroid);
  }

  #handleModalClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-meta-browser-close]') || event.target === this.#modal) this.#close();
  };

  #handleModalCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  #handleKeydown = (event) => {
    if (!this.#modal.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#close();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusableElements = [this.#closeButton, this.#externalLink].filter(
      (element) => element instanceof HTMLElement && element.tabIndex >= 0
    );
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };
}

new MetaBrowserCheckoutRecovery();
